/**
 * MAIN-879: import progress from the job's real state.
 *
 * The worker records the step a PROCESSING job is on (job.stage). Nothing here
 * invents a percentage: steps are shown as done / current / to do, under an
 * indeterminate loader, because no step can say how far through it is.
 */

export const IMPORT_STEPS = Object.freeze([
  { key: "QUEUED", label: "Queued" },
  { key: "RETRIEVING_SOURCE", label: "Retrieving the menu" },
  { key: "PARSING", label: "Reading the menu" },
  { key: "MATCHING_CATEGORIES", label: "Matching categories" },
  { key: "PREPARING_REVIEW", label: "Preparing items for review" },
]);

const STEP_KEYS = IMPORT_STEPS.map((step) => step.key);

/** True while a job is still being worked on and worth watching. */
export function isImportInProgress(job) {
  return job?.status === "QUEUED" || job?.status === "PROCESSING";
}

/** The step a job is on. A just-claimed PROCESSING job has no stage yet. */
export function currentStepKey(job) {
  if (job?.status === "QUEUED") return "QUEUED";
  if (job?.status !== "PROCESSING") return null;
  return STEP_KEYS.includes(job.stage) ? job.stage : "RETRIEVING_SOURCE";
}

/**
 * Everything the progress UI shows:
 * { state: "waiting"|"active"|"ready"|"failed", headline, detail, steps }
 * where each step is { key, label, status: "done"|"current"|"todo" }.
 */
// MAIN-879/847: what kind of failure it was, from the API's failureCategory,
// so a temporary problem is not presented like a final refusal.
export const FAILURE_CATEGORY_LABELS = Object.freeze({
  temporary: "Temporary problem. Trying again in a few minutes may work.",
  access_restricted: "The source refused access. This isn't retried automatically.",
  not_retrievable: "The page couldn't be retrieved. Check the link.",
  no_menu_found: "The page was read, but no menu items were found.",
  unsafe_content: "The page was reported as unsafe. This isn't retried automatically.",
  off_site: "The content came from a different website, so it wasn't imported.",
  not_available: "This import isn't available for this account yet.",
});

export function failureCategoryLabel(category) {
  return FAILURE_CATEGORY_LABELS[category] || null;
}

export function getImportProgress(job) {
  if (!job) {
    return {
      state: "waiting",
      headline: "Starting your import…",
      detail: null,
      steps: IMPORT_STEPS.map((step) => ({ ...step, status: "todo" })),
    };
  }
  if (job.status === "FAILED") {
    return {
      state: "failed",
      headline: "This import didn't finish.",
      category: failureCategoryLabel(job.failureCategory),
      detail: job.errorMessage || "Something went wrong reading the menu.",
      steps: [],
    };
  }
  if (job.status === "NEEDS_REVIEW" || job.status === "APPLIED") {
    return {
      state: "ready",
      headline: "Ready for review.",
      detail: null,
      steps: IMPORT_STEPS.map((step) => ({ ...step, status: "done" })),
    };
  }
  const current = currentStepKey(job);
  const currentIndex = STEP_KEYS.indexOf(current);
  const steps = IMPORT_STEPS.map((step, index) => ({
    ...step,
    status: index < currentIndex ? "done" : index === currentIndex ? "current" : "todo",
  }));
  const label = IMPORT_STEPS[currentIndex]?.label || "Working";
  return {
    state: "active",
    headline: current === "QUEUED" ? "Queued — your import will start shortly." : `${label}…`,
    // Long PDFs take minutes on the reading step; say so, so it never looks frozen.
    detail:
      current === "PARSING"
        ? "Longer menus can take a few minutes. You can leave this page — the import keeps going."
        : "You can leave this page — the import keeps going.",
    steps,
  };
}
